export const PHONE_MASK = "+7(999) 999-99-99";
export const PHONE_PLACEHOLDER = "+7(___)-___-__-__";

/** Показывает дату и время */
export const formatDateTime = (
    dateTimeString: string | null | undefined,
    isMsk: boolean = false,
    showSeconds: boolean = false
) => {
    if (!dateTimeString) return 'Нет данных';

    return new Date(dateTimeString).toLocaleString('ru-RU', {
        ...(isMsk && { timeZone: "Europe/Moscow" }),
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: showSeconds
            ? '2-digit'
            : undefined,
    });
};

/** Показывает календарную дату и время без смещения */
export const formatLocalDateTime = (dateTimeString: string | undefined) => {
    if (!dateTimeString) return 'Нет данных';

    const date = new Date(dateTimeString);

    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();

    const localDate = new Date(year, month, day, hours, minutes);

    return localDate.toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

/** Показывает календарную дату */
export const formatDate = (
    dateString: string | number | Date | null | undefined,
    isMsk: boolean = false
) => {
    if (!dateString) return 'Нет данных'

    return new Date(dateString).toLocaleDateString('ru-RU', {
        ...(isMsk && { timeZone: "Europe/Moscow" }),
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

export { cleanPhone } from '@/shared/lib/phone';

/** Возвращает календарную дату в формате год-месяц-день */
export const formatDateToDateOnly = (date: Date | null): string | null => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

/** Возвращает начало московского календарного дня в UTC */
export const formatDateMSKtoUTC = (date: Date | null): string | null => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    const utcDate = new Date(Date.UTC(year, month, day, -3, 0, 0, 0));

    return utcDate.toISOString();
};

/** Форматирует необязательное текстовое или числовое значение. */
export const formatOptionalText = (
    value: string | number | null | undefined,
    fallback = 'Не указано',
) => value === null || value === undefined || value === ''
        ? fallback
        : value;

/** Показывает ответ да или нет для необязательного значения */
export const formatOptionalBoolean = (value: boolean | null | undefined) =>
    value === true
        ? 'Да'
        : value === false
            ? 'Нет'
            : 'Не указано';

/**
 * Универсальное форматирование чисел (целые и дробные)
 * @param value Число или null/undefined
 * @param options Настройки форматирования
 * @returns Отформатированная строка
 */
export const formatOptionalNumber = (
    value: number | null | undefined,
    options: {
        decimals?: number;          // Количество знаков после запятой
        fallback?: string;          // Текст при отсутствии значения
        thousandSeparator?: boolean;// Разделитель тысяч (true для "1 234")
        currency?: string;          // Валюта (" руб.", "$")
        compact?: boolean;          // Сокращённый формат ("1.2K" вместо "1200")
    } = {}
): string => {
    const {
        decimals = 2,
        fallback = 'Не указано',
        thousandSeparator = true,
        currency = '',
        compact = false
    } = options;

    if (value === null || value === undefined) return fallback;

    // Компактный формат (1.2K, 3.5M)
    if (compact && Math.abs(value) >= 1000) {
        const units = ['', 'K', 'M', 'B', 'T'];
        const order = Math.floor(Math.log10(Math.abs(value)) / 3);
        const unit = units[order] || '';
        const num = (value / Math.pow(1000, order)).toFixed(1);

        return `${num}${unit} ${currency}`;
    }

    // Обычный формат
    const formatter = new Intl.NumberFormat('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
        useGrouping: thousandSeparator
    });

    return `${formatter.format(value)} ${currency}`;
};
